import { useEffect, useRef, useCallback } from 'react';
import { authAPI } from '../services/api';

const useAutoRefresh = (isLoggedIn, onLogout) => {
  const intervalRef = useRef(null);
  const isRefreshingRef = useRef(false);

  // 토큰 리프레시 함수
  const refreshToken = useCallback(async () => {
    if (isRefreshingRef.current) return;
    
    try {
      isRefreshingRef.current = true;
      await authAPI.refreshToken();
      console.log('Token refreshed successfully');
    } catch (error) {
      console.error('Auto refresh failed:', error);
      // 리프레시 실패 시 로그아웃 처리
      if (onLogout) {
        onLogout();
      }
    } finally {
      isRefreshingRef.current = false;
    }
  }, [onLogout]);

  // 토큰 만료 시간 계산
  const getTokenExpiryTime = useCallback((token) => {
    try {
      if (!token || typeof token !== 'string') {
        return null;
      }
      
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      
      // Base64URL 디코딩
      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      const payload = JSON.parse(jsonPayload);
      return payload.exp * 1000; // 밀리초로 변환
    } catch (error) {
      console.error('Failed to parse token:', error);
      return null;
    }
  }, []);

  // 다음 리프레시 시간 계산 (토큰 만료 5분 전)
  const getNextRefreshTime = useCallback(() => {
    const token = localStorage.getItem('authToken');
    
    if (!token) {
      return 14 * 60 * 1000; // 기본 14분
    }

    const expiryTime = getTokenExpiryTime(token);
    if (!expiryTime) {
      return 14 * 60 * 1000;
    }

    const fiveMinutesBeforeExpiry = expiryTime - (5 * 60 * 1000);
    const timeUntilRefresh = fiveMinutesBeforeExpiry - Date.now();
    
    // 최소 1분, 최대 14분으로 제한
    return Math.max(60 * 1000, Math.min(timeUntilRefresh, 14 * 60 * 1000));
  }, [getTokenExpiryTime]);

  // 자동 리프레시 시작
  const startAutoRefresh = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    const scheduleNextRefresh = () => {
      const nextRefreshTime = getNextRefreshTime();
      console.log(`Next token refresh scheduled in ${Math.round(nextRefreshTime / 1000 / 60)} minutes`);
      
      intervalRef.current = setTimeout(() => {
        refreshToken().then(() => {
          // 리프레시 후 다음 스케줄 설정
          scheduleNextRefresh();
        });
      }, nextRefreshTime);
    };

    scheduleNextRefresh();
  }, [refreshToken, getNextRefreshTime]);

  // 자동 리프레시 중지
  const stopAutoRefresh = useCallback(() => {
    if (intervalRef.current) {
      clearTimeout(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // 페이지 가시성 변경 처리
  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      // 페이지가 숨겨졌을 때는 리프레시 중지
      stopAutoRefresh();
    } else {
      // 페이지가 다시 보일 때 리프레시 시작 및 즉시 토큰 갱신
      if (isLoggedIn) {
        refreshToken();
        startAutoRefresh();
      }
    }
  }, [isLoggedIn, refreshToken, startAutoRefresh, stopAutoRefresh]);

  // 페이지 포커스/블러 처리
  const handleFocus = useCallback(() => {
    if (isLoggedIn) {
      refreshToken();
      startAutoRefresh();
    }
  }, [isLoggedIn, refreshToken, startAutoRefresh]);

  const handleBlur = useCallback(() => {
    stopAutoRefresh();
  }, [stopAutoRefresh]);

  // 컴포넌트 마운트/언마운트 시 처리
  useEffect(() => {
    if (isLoggedIn) {
      // 로그인 상태일 때 자동 리프레시 시작
      startAutoRefresh();
      
      // 이벤트 리스너 등록
      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('focus', handleFocus);
      window.addEventListener('blur', handleBlur);

      return () => {
        // 정리 함수
        stopAutoRefresh();
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('focus', handleFocus);
        window.removeEventListener('blur', handleBlur);
      };
    } else {
      // 로그아웃 상태일 때 자동 리프레시 중지
      stopAutoRefresh();
    }
  }, [isLoggedIn, startAutoRefresh, stopAutoRefresh, handleVisibilityChange, handleFocus, handleBlur]);

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      stopAutoRefresh();
    };
  }, [stopAutoRefresh]);

  return {
    refreshToken,
    startAutoRefresh,
    stopAutoRefresh
  };
};

export default useAutoRefresh;
